import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import ImageInput from "@/components/ImageInput";
import VideoInput from "@/components/VideoInput";
import {
  FloppyDiskIcon, ArrowClockwiseIcon, EyeIcon, ImageIcon, PlusIcon, TrashIcon, PaletteIcon, VideoCameraIcon,
  LightbulbIcon, LockKeyIcon
} from "@phosphor-icons/react";

/**
 * Editor de la Landing — solo accesible para admin/owner.
 * Permite modificar: colores, hero, about, stats, products, testimonials, gallery, contact, footer.
 */
export default function LandingEditor() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/landing/settings");
      setCfg(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.key; delete payload.updated_at; delete payload.updated_by;
      const { data } = await api.put("/landing/settings", payload);
      setCfg(data);
      toast.success("Landing actualizada");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("¿Resetear toda la landing a valores por defecto? Esta acción no se puede deshacer.")) return;
    setResetting(true);
    try {
      await api.post("/landing/reset");
      toast.success("Landing reseteada");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setResetting(false); }
  };

  if (!cfg) return <Layout title="Editor de Landing"><div className="text-text-muted">Cargando…</div></Layout>;

  const tabs = [
    { k: "hero", l: "Hero" },
    { k: "corn", l: "Animación Maíz" },
    { k: "nav", l: "Menú Nav" },
    { k: "about", l: "Nosotros" },
    { k: "products", l: "Productos" },
    { k: "stats", l: "Indicadores" },
    { k: "testimonials", l: "Testimoniales" },
    { k: "gallery", l: "Galería" },
    { k: "contact", l: "Contacto" },
    { k: "footer", l: "Footer" },
  ];

  return (
    <Layout
      title="Editor de Landing"
      subtitle="Personaliza textos, imágenes, videos y datos de la página pública"
      actions={
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="landing-preview-btn"
             className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-md hover:border-agro-green/40">
            <EyeIcon size={14} /> Ver landing
          </a>
          <button onClick={resetAll} disabled={resetting} data-testid="landing-reset-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-red-500/40 text-red-400 rounded-md hover:bg-red-500/10 disabled:opacity-50">
            <ArrowClockwiseIcon size={14} /> Resetear
          </button>
          <button onClick={save} disabled={saving} data-testid="landing-save-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-50">
            <FloppyDiskIcon size={14} weight="bold" /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      }
    >
      <div className="flex gap-1 mb-5 border-b border-border/60 overflow-x-auto" data-testid="editor-tabs">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`editor-tab-${t.k}`}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap transition ${tab === t.k ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border/60 rounded-2xl p-6 space-y-4">
        {tab === "hero" && <HeroEditor cfg={cfg} onChange={(hero) => setCfg({ ...cfg, hero })} />}
        {tab === "corn" && <CornEditor data={cfg.corn || {}} onChange={(corn) => setCfg({ ...cfg, corn })} />}
        {tab === "nav" && <NavEditor data={cfg.nav || {}} onChange={(nav) => setCfg({ ...cfg, nav })} />}
        {tab === "about" && <AboutEditor cfg={cfg} onChange={(about) => setCfg({ ...cfg, about })} />}
        {tab === "products" && <ListEditor items={cfg.products || []} fields={["title","desc","image","icon"]} labels={{title:"Título",desc:"Descripción",image:"URL imagen",icon:"Icono (plant|flask|drop|spray|mountains|package)"}} onChange={(products) => setCfg({ ...cfg, products })} />}
        {tab === "stats" && <ListEditor items={cfg.stats || []} fields={["value","suffix","label"]} labels={{value:"Número (ej. 15)",suffix:"Sufijo (+ o %)",label:"Etiqueta"}} onChange={(stats) => setCfg({ ...cfg, stats })} />}
        {tab === "testimonials" && <ListEditor items={cfg.testimonials || []} fields={["name","role","text","avatar"]} labels={{name:"Nombre",role:"Rol/Empresa",text:"Comentario",avatar:"URL foto"}} onChange={(testimonials) => setCfg({ ...cfg, testimonials })} />}
        {tab === "gallery" && <GalleryEditor items={cfg.gallery || []} onChange={(gallery) => setCfg({ ...cfg, gallery })} />}
        {tab === "contact" && <DictEditor data={cfg.contact || {}} labels={{phone_choluteca:"Tel. Choluteca",phone_san_marcos:"Tel. San Marcos",whatsapp_choluteca:"WhatsApp Choluteca URL",whatsapp_san_marcos:"WhatsApp San Marcos URL",email:"Correo",address:"Dirección",hours:"Horarios"}} onChange={(contact) => setCfg({ ...cfg, contact })} />}
        {tab === "footer" && <DictEditor data={cfg.footer || {}} labels={{description:"Descripción",facebook:"Facebook URL",instagram:"Instagram URL",linkedin:"LinkedIn URL"}} onChange={(footer) => setCfg({ ...cfg, footer })} />}
      </div>

      <div className="mt-4 text-xs text-text-muted">
        <LightbulbIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Después de guardar, abre la landing en otra pestaña ({" "}<a href="/" target="_blank" rel="noopener noreferrer" className="text-agro-green hover:underline">/</a>) para ver los cambios. Refresca con <kbd>Ctrl+F5</kbd> si no aparecen.
      </div>
    </Layout>
  );
}

// ============================================================
function HeroEditor({ cfg, onChange }) {
  const h = cfg.hero || {};
  const upd = (k, v) => onChange({ ...h, [k]: v });
  const bgType = h.background_type || (h.background_video ? "video" : "image");
  const overlay = h.overlay_opacity ?? 15;
  return (
    <div className="space-y-4">
      <Field label="Badge superior"><Input value={h.badge || ""} onChange={(v) => upd("badge", v)} /></Field>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Título — parte 1"><Input value={h.title_pre || ""} onChange={(v) => upd("title_pre", v)} /></Field>
        <Field label="Título — resaltado (verde)"><Input value={h.title_highlight || ""} onChange={(v) => upd("title_highlight", v)} /></Field>
        <Field label="Título — parte 3"><Input value={h.title_post || ""} onChange={(v) => upd("title_post", v)} /></Field>
      </div>
      <Field label="Subtítulo"><TextArea value={h.subtitle || ""} onChange={(v) => upd("subtitle", v)} /></Field>

      {/* === Fondo del Hero: toggle Imagen / Video === */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="hero-bg-controls">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white">Fondo del Hero</div>
            <div className="text-xs text-white/50">Elige entre imagen estática o video en 4K</div>
          </div>
          <div className="inline-flex rounded-lg border border-white/10 bg-black/40 p-1">
            <button
              type="button"
              onClick={() => upd("background_type", "image")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${bgType === "image" ? "bg-agro-green text-black" : "text-white/70 hover:text-white"}`}
              data-testid="hero-bg-type-image"
            >Imagen</button>
            <button
              type="button"
              onClick={() => upd("background_type", "video")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${bgType === "video" ? "bg-agro-green text-black" : "text-white/70 hover:text-white"}`}
              data-testid="hero-bg-type-video"
            >Video</button>
          </div>
        </div>

        {bgType === "image" ? (
          <Field label="Imagen de fondo del hero" hint="Foto grande de fondo. URL o sube una nueva.">
            <ImageInput value={h.background_image || ""} onChange={(v) => upd("background_image", v)} folder="landing" />
          </Field>
        ) : (
          <>
            <Field label="URL del video de fondo (MP4)" hint="Video 4K recomendado (máx 100 MB). Se reproduce en loop, silenciado y autoplay. Pulsa Subir para cargar el archivo directo.">
              <VideoInput value={h.background_video || ""} onChange={(v) => upd("background_video", v)} folder="landing" testid="hero-video-input" />
            </Field>
            <Field label="Poster / imagen de respaldo" hint="Se muestra mientras el video carga o si falla la reproducción.">
              <ImageInput value={h.background_image || ""} onChange={(v) => upd("background_image", v)} folder="landing" />
            </Field>
          </>
        )}

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold text-white/80">Overlay oscuro sobre el fondo</label>
            <span className="text-xs text-agro-green font-mono" data-testid="hero-overlay-value">{overlay}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="80"
            step="1"
            value={overlay}
            onChange={(e) => upd("overlay_opacity", Number(e.target.value))}
            className="w-full accent-agro-green"
            data-testid="hero-overlay-slider"
          />
          <div className="text-[11px] text-white/40 mt-1">0% = sin overlay · 15% recomendado · 80% máximo</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Texto botón primario"><Input value={h.cta_primary || ""} onChange={(v) => upd("cta_primary", v)} /></Field>
        <Field label="Texto botón secundario"><Input value={h.cta_secondary || ""} onChange={(v) => upd("cta_secondary", v)} /></Field>
      </div>
    </div>
  );
}

function AboutEditor({ cfg, onChange }) {
  const a = cfg.about || {};
  const upd = (k, v) => onChange({ ...a, [k]: v });
  return (
    <div className="space-y-4">
      <Field label="Etiqueta de sección"><Input value={a.label || ""} onChange={(v) => upd("label", v)} /></Field>
      <Field label="Título"><Input value={a.title || ""} onChange={(v) => upd("title", v)} /></Field>
      <Field label="Párrafo 1"><TextArea value={a.paragraph1 || ""} onChange={(v) => upd("paragraph1", v)} /></Field>
      <Field label="Párrafo 2"><TextArea value={a.paragraph2 || ""} onChange={(v) => upd("paragraph2", v)} /></Field>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Imagen principal"><ImageInput value={a.image1 || ""} onChange={(v) => upd("image1", v)} folder="landing" /></Field>
        <Field label="Imagen secundaria"><ImageInput value={a.image2 || ""} onChange={(v) => upd("image2", v)} folder="landing" /></Field>
      </div>
      <Field label="URL del video del play button" hint="YouTube o MP4 directo"><Input value={a.video_url || ""} onChange={(v) => upd("video_url", v)} icon={VideoCameraIcon} /></Field>
      <Field label="Bullets (uno por línea)">
        <TextArea
          rows={4}
          value={(a.bullets || []).join("\n")}
          onChange={(v) => upd("bullets", v.split("\n").filter((x) => x.trim()))}
        />
      </Field>
    </div>
  );
}

function GalleryEditor({ items, onChange }) {
  const add = () => onChange([...items, ""]);
  const upd = (i, v) => { const c = [...items]; c[i] = v; onChange(c); };
  const rm = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div className="space-y-3">
      {items.map((url, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1">
            <ImageInput value={url} onChange={(v) => upd(i, v)} folder="landing" placeholder={`Imagen #${i + 1}`} />
          </div>
          <button aria-label="Eliminar" onClick={() => rm(i)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-md mt-1" data-testid={`gallery-del-${i}`}><TrashIcon size={16} /></button>
        </div>
      ))}
      <button onClick={add} data-testid="gallery-add" className="inline-flex items-center gap-1.5 text-sm text-agro-green hover:text-agro-yellow"><PlusIcon size={14} weight="bold" /> Agregar imagen</button>
    </div>
  );
}

function ListEditor({ items, fields, labels, onChange }) {
  const blank = Object.fromEntries(fields.map((f) => [f, ""]));
  const add = () => onChange([...items, { ...blank }]);
  const upd = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const rm = (i) => onChange(items.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const c = [...items];
    const j = i + dir;
    if (j < 0 || j >= c.length) return;
    [c[i], c[j]] = [c[j], c[i]];
    onChange(c);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="bg-app/60 border border-border/60 rounded-xl p-4 space-y-2" data-testid={`list-item-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-text-muted">Elemento #{i + 1}</div>
            <div className="flex gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-2 py-1 hover:bg-white/5 rounded disabled:opacity-30">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs px-2 py-1 hover:bg-white/5 rounded disabled:opacity-30">↓</button>
              <button aria-label="Eliminar" onClick={() => rm(i)} className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded"><TrashIcon size={14} /></button>
            </div>
          </div>
          <div className={`grid ${fields.length > 2 ? "md:grid-cols-2" : ""} gap-2`}>
            {fields.map((f) => (
              <Field key={f} label={labels[f] || f} small>
                {f === "desc" || f === "text" ? (
                  <TextArea rows={2} value={it[f] || ""} onChange={(v) => upd(i, f, v)} />
                ) : f === "image" || f === "avatar" ? (
                  <ImageInput value={it[f] || ""} onChange={(v) => upd(i, f, v)} folder="landing" />
                ) : (
                  <Input value={it[f] || ""} onChange={(v) => upd(i, f, f === "value" ? Number(v) || 0 : v)} type={f === "value" ? "number" : "text"} />
                )}
              </Field>
            ))}
          </div>
        </div>
      ))}
      <button onClick={add} data-testid="list-add" className="inline-flex items-center gap-1.5 text-sm text-agro-green hover:text-agro-yellow"><PlusIcon size={14} weight="bold" /> Agregar</button>
    </div>
  );
}

function DictEditor({ data, labels, onChange }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {Object.keys(labels).map((k) => (
        <Field key={k} label={labels[k]}>
          {k === "description" ? (
            <TextArea rows={3} value={data[k] || ""} onChange={(v) => onChange({ ...data, [k]: v })} />
          ) : (
            <Input value={data[k] || ""} onChange={(v) => onChange({ ...data, [k]: v })} />
          )}
        </Field>
      ))}
    </div>
  );
}

// ============================================================
function CornEditor({ data, onChange }) {
  const enabled = data.enabled !== false;
  const stages = (data.stages && data.stages.length === 3)
    ? data.stages
    : [
        { eyebrow: "01 — SEMILLA",     title: "Cada semilla",  subtitle: "guarda un código." },
        { eyebrow: "02 — CRECIMIENTO", title: "Vida que sube", subtitle: "fuerte y sana." },
        { eyebrow: "03 — COSECHA",     title: "Producción",    subtitle: "garantizada y viva." },
      ];
  const stats = (data.stats && data.stats.length === 3)
    ? data.stats
    : [
        { value: "+38%",  label: "Rendimiento" },
        { value: "99.4%", label: "Pureza" },
        { value: "GMO−",  label: "Verificado" },
      ];

  const updStage = (i, k, v) => {
    const next = stages.map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    onChange({ ...data, enabled, stages: next, stats });
  };
  const updStat = (i, k, v) => {
    const next = stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    onChange({ ...data, enabled, stages, stats: next });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between bg-app/60 border border-border/60 rounded-xl p-4">
        <div>
          <div className="text-sm font-semibold text-text-primary">Sección visible</div>
          <div className="text-xs text-text-muted">Si lo apagas, la animación de la semilla → planta desaparece de la landing.</div>
        </div>
        <button
          onClick={() => onChange({ ...data, enabled: !enabled, stages, stats })}
          data-testid="corn-toggle"
          className={`relative w-12 h-6 rounded-full transition ${enabled ? "bg-agro-green" : "bg-white/15"}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${enabled ? "left-6" : "left-0.5"}`} />
        </button>
      </div>

      <div>
        <div className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-2">Etapas narrativas</div>
        <div className="space-y-3">
          {stages.map((s, i) => (
            <div key={i} className="bg-app/60 border border-border/60 rounded-xl p-4 space-y-2" data-testid={`corn-stage-${i}`}>
              <div className="text-xs text-text-muted mb-1">Etapa #{i + 1}</div>
              <div className="grid md:grid-cols-3 gap-2">
                <Field label="Eyebrow (encima del título)" small>
                  <Input value={s.eyebrow || ""} onChange={(v) => updStage(i, "eyebrow", v)} />
                </Field>
                <Field label="Título grande" small>
                  <Input value={s.title || ""} onChange={(v) => updStage(i, "title", v)} />
                </Field>
                <Field label="Subtítulo (cursiva)" small>
                  <Input value={s.subtitle || ""} onChange={(v) => updStage(i, "subtitle", v)} />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-2">Stats finales (al llegar a Cosecha)</div>
        <div className="grid md:grid-cols-3 gap-2">
          {stats.map((st, i) => (
            <div key={i} className="bg-app/60 border border-border/60 rounded-xl p-3 space-y-2" data-testid={`corn-stat-${i}`}>
              <Field label={`Stat #${i + 1} — Valor`} small>
                <Input value={st.value || ""} onChange={(v) => updStat(i, "value", v)} />
              </Field>
              <Field label="Etiqueta" small>
                <Input value={st.label || ""} onChange={(v) => updStat(i, "label", v)} />
              </Field>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-text-muted bg-app/30 border border-border/40 rounded-lg p-3">
        ℹ️ El video (frames de la planta creciendo) está embebido en el código por motivos técnicos. Si quieres cambiarlo, contáctame.
      </div>
    </div>
  );
}

// ============================================================
function NavEditor({ data, onChange }) {
  const items = Array.isArray(data.items) && data.items.length > 0
    ? data.items
    : [
        { id: "top",       label: "Inicio",    enabled: true },
        { id: "about",     label: "Nosotros",  enabled: true },
        { id: "productos", label: "Productos", enabled: true },
        { id: "cobertura", label: "Cobertura", enabled: true },
        { id: "galeria",   label: "Galería",   enabled: true },
        { id: "contacto",  label: "Contacto",  enabled: true },
      ];

  const upd = (i, k, v) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it));
    onChange({ ...data, items: next });
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const c = [...items];
    [c[i], c[j]] = [c[j], c[i]];
    onChange({ ...data, items: c });
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-2">Items del menú</div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.id} className="bg-app/60 border border-border/60 rounded-xl p-3 flex items-center gap-3" data-testid={`nav-item-${it.id}`}>
              <div className="text-[10px] uppercase tracking-widest text-text-muted bg-white/[0.04] px-2 py-1 rounded font-mono">#{it.id}</div>
              <div className="flex-1">
                <Input value={it.label || ""} onChange={(v) => upd(i, "label", v)} placeholder="Texto visible" />
              </div>
              <button
                onClick={() => upd(i, "enabled", it.enabled === false ? true : false)}
                className={`relative w-11 h-6 rounded-full transition flex-shrink-0 ${it.enabled !== false ? "bg-agro-green" : "bg-white/15"}`}
                data-testid={`nav-toggle-${it.id}`}
                title="Mostrar/Ocultar item"
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${it.enabled !== false ? "left-5" : "left-0.5"}`} />
              </button>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-2 py-1 hover:bg-white/5 rounded disabled:opacity-30">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs px-2 py-1 hover:bg-white/5 rounded disabled:opacity-30">↓</button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[10px] text-text-muted">
          <LockKeyIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Los IDs (#top, #about, etc.) NO se pueden cambiar — están atados a las secciones de la landing.
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-2">Botón CTA del menú</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Texto cuando NO está logueado" hint="Lo verá el visitante anónimo">
            <Input value={data.cta_label || ""} onChange={(v) => onChange({ ...data, items, cta_label: v })} placeholder="Iniciar Sesión" />
          </Field>
          <Field label="Texto cuando SÍ está logueado" hint="Lo verá el usuario que ya hizo login">
            <Input value={data.cta_label_logged || ""} onChange={(v) => onChange({ ...data, items, cta_label_logged: v })} placeholder="Ir al ERP" />
          </Field>
        </div>
      </div>
    </div>
  );
}

// ============================================================
function Field({ label, hint, children, small }) {
  return (
    <label className="block">
      <span className={`block ${small ? "text-[11px]" : "text-xs"} font-medium text-text-secondary mb-1.5`}>{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-text-muted mt-1">{hint}</span>}
    </label>
  );
}
function Input({ value, onChange, placeholder, type = "text", icon: Icon }) {
  return (
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-app border border-border rounded-md py-2 text-sm focus:border-agro-green outline-none ${Icon ? "pl-8 pr-3" : "px-3"}`}
      />
    </div>
  );
}
function TextArea({ value, onChange, rows = 3 }) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none resize-none"
    />
  );
}
